/**
 * Human Resources access levels: six keys, six levels, from the super admin
 * down. The platform enforces the keys on every request (migration 0065);
 * this file only says which level a set of keys amounts to and what each
 * level may do, so the screens can hide what a person cannot use.
 */

export const HR_KEYS = ["hr:read", "hr:write", "hr:pay", "hr:confidential", "hr:approve", "hr:admin"] as const;
export type HrKey = (typeof HR_KEYS)[number];

export type HrLevelKey = "super_admin" | "manager" | "payroll" | "officer" | "assistant" | "executive" | "none";

export interface HrLevel {
  key: HrLevelKey;
  /** the platform role that carries it */
  role: string | null;
  name: string;
  who: string;
  keys: HrKey[];
}

export const HR_LEVELS: HrLevel[] = [
  {
    key: "super_admin",
    role: "hr_super_admin",
    name: "HR Super Admin",
    who: "Full access: every file, pay, reviews and discipline, approvals, the labour rules, and who gets which HR level.",
    keys: ["hr:read", "hr:write", "hr:pay", "hr:confidential", "hr:approve", "hr:admin"],
  },
  {
    key: "manager",
    role: "hr_manager",
    name: "HR Manager",
    who: "Every file, pay, reviews and discipline; approves leave and letters. Cannot change the rules or the levels.",
    keys: ["hr:read", "hr:write", "hr:pay", "hr:confidential", "hr:approve"],
  },
  {
    key: "payroll",
    role: "payroll_officer",
    name: "Payroll Officer",
    who: "Pay, overtime, manpower cost and end of service. Sees no reviews or discipline and approves nothing.",
    keys: ["hr:read", "hr:write", "hr:pay"],
  },
  {
    key: "officer",
    role: "hr_officer",
    name: "HR Officer",
    who: "Keeps the staff files, attendance and leave, issues letters and approves requests. Does not see pay.",
    keys: ["hr:read", "hr:write", "hr:approve"],
  },
  {
    key: "assistant",
    role: "hr_assistant",
    name: "HR Assistant",
    who: "Admin staff: personal details, photos, attendance, and leave and letter requests for others to approve. No pay.",
    keys: ["hr:read", "hr:write"],
  },
  {
    key: "executive",
    role: "accountable_manager",
    name: "Executive (cost oversight)",
    who: "Reads the workforce and its cost. Changes nothing.",
    keys: ["hr:read", "hr:pay"],
  },
  {
    key: "none",
    role: null,
    name: "No HR access",
    who: "Sees no one's file or pay.",
    keys: [],
  },
];

/** What each key lets somebody do, in the words of the access screen. */
export const HR_ABILITIES: { label: string; key: HrKey }[] = [
  { label: "See the register and each staff file", key: "hr:read" },
  { label: "Add people, edit details and photos, record attendance, file requests", key: "hr:write" },
  { label: "See and change pay; manpower cost, labour cost, end of service", key: "hr:pay" },
  { label: "Read and write performance reviews and disciplinary actions", key: "hr:confidential" },
  { label: "Approve or reject leave and issue letters", key: "hr:approve" },
  { label: "Change the labour rules and give people HR levels", key: "hr:admin" },
];

export function levelOfKeys(keys: readonly string[]): HrLevel {
  const has = (k: HrKey) => keys.includes(k);
  for (const l of HR_LEVELS) if (l.keys.length && l.keys.every(has) && l.keys.length === HR_KEYS.filter(has).length) return l;
  /* not an exact level: the highest one whose keys it holds */
  return HR_LEVELS.find((l) => l.keys.length && l.keys.every(has)) ?? HR_LEVELS[HR_LEVELS.length - 1];
}

export const levelByKey = (k: HrLevelKey) => HR_LEVELS.find((l) => l.key === k) ?? HR_LEVELS[HR_LEVELS.length - 1];

export interface HrCan {
  read: boolean;
  write: boolean;
  pay: boolean;
  confidential: boolean;
  approve: boolean;
  admin: boolean;
}

export function canOf(keys: readonly string[]): HrCan {
  return {
    read: keys.includes("hr:read"),
    write: keys.includes("hr:write"),
    pay: keys.includes("hr:pay"),
    confidential: keys.includes("hr:confidential"),
    approve: keys.includes("hr:approve"),
    admin: keys.includes("hr:admin"),
  };
}

export interface HrAccessHolder {
  id: string;
  displayName: string;
  email: string | null;
}

export interface HrAccessView {
  roles: { key: string; name: string; description: string; permissions: string[]; holders: HrAccessHolder[] }[];
  users: { id: string; displayName: string; email: string | null }[];
  canAssign: boolean;
}
